'use client';
import { Button } from '@svs/ui/react/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@svs/ui/react/drawer';
import type { PropsWithChildren, ReactNode } from 'react';

import { cn } from '../utils/entry.js';

type BetslipProps = PropsWithChildren<{
  className: string;
  isPopulatedCoupon: boolean;
  betslipContent: ReactNode;
  betslipMobileHeight?: string;
  onOpenDrawerClick?: () => void;
}>;

/**
 * The betslip component, works like a drawer to display information about the coupon that is actively being filled.
 *
 * @param root0 input object
 * @param root0.children the children to pass down
 * @param root0.className the classname to be used
 * @param root0.isPopulatedCoupon if the coupon is populated at all.
 * @param root0.betslipContent the overview component to be displayed in the drawer
 * @param root0.betslipMobileHeight the height of the betslip on mobile, defaults to 77px
 * @param root0.onOpenDrawerClick function to be called when the drawer is opened, used for tracking purposes
 * @returns the complete betslip
 */
export default function Betslip({
  children,
  className,
  isPopulatedCoupon,
  betslipContent,
  betslipMobileHeight,
  onOpenDrawerClick,
}: BetslipProps): ReactNode {
  const upwardXlShadow = 'shadow-[0_-20px_25px_-5px_rgba(0,0,0,0.1),0_-8px_10px_-6px_rgba(0,0,0,0.1)]';
  const classNames = [
    'grid',
    'bg-surface-base',
    'gap-x-[12px]',
    'p-[16px]',
    'lg:p-[12px]',
    'pt-[4px]',
    'lg:pt-[12px]',
    `h-[${betslipMobileHeight ?? '77px'}]`,
    'lg:h-[70px]',
    'text-layer-fg-muted',
  ];
  if (className.length > 0) {
    classNames.push(className);
  } else {
    classNames.push('grid-flow-col');
  }
  const handleOnClick = (): void => {
    if (onOpenDrawerClick) {
      onOpenDrawerClick();
    }
  };
  return (
    <>
      <div
        className={cn(
          'invisible fixed bottom-0 left-0',
          isPopulatedCoupon && 'visible z-40 hidden w-full lg:block',
          upwardXlShadow
        )}
      >
        <div className="bg-surface-base mx-auto w-full max-w-[1440px] px-[36px]">
          <div className={cn(classNames.join(' '), 'items-center')}>{children}</div>
        </div>
      </div>
      <Drawer>
        <DrawerTrigger onClick={handleOnClick} asChild>
          <div
            className={cn(
              'invisible fixed bottom-0 left-0',
              isPopulatedCoupon && 'bg-surface-base visible z-40 grid w-full cursor-pointer lg:hidden',
              upwardXlShadow
            )}
          >
            <div
              className={cn('bg-surface-base grid h-[8px] w-full items-end justify-items-center justify-self-center')}
            >
              <div className="bg-layer-fg-muted bottom-0 h-[2px] w-[5%] rounded" />
            </div>
            <div className={classNames.join(' ')}>{children}</div>
          </div>
        </DrawerTrigger>
        <DrawerContent className="mb-3">
          <DrawerHeader>
            <DrawerTitle className="text-center text-lg">Kupongöversikt</DrawerTitle>
          </DrawerHeader>
          {betslipContent}
          <DrawerFooter className="pt-0">
            <DrawerClose asChild>
              <Button variant="outline">Stäng</Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}
